import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DailyIframe from "@daily-co/daily-js";
import { DailyProvider } from "@daily-co/daily-react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebase.js";
import { getDailyCredentialsForRole } from "../../utils/lessonLinks.js";
import LessonCall from "./LessonCall.jsx";
import styles from "./LessonRoom.module.css";

function formatWhen(start, end) {
  if (!start) return "";
  const opts = { hour: "2-digit", minute: "2-digit" };
  const s = start.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    ...opts,
  });
  const e = end
    ? end.toLocaleTimeString("en-GB", opts)
    : "";
  return e ? `${s} – ${e}` : s;
}

export default function LessonRoom() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const role = useSelector((state) => state.login?.role);
  const uid = auth.currentUser?.uid;

  const [event, setEvent] = useState(null);
  const [secrets, setSecrets] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callObject, setCallObject] = useState(null);
  const [joinError, setJoinError] = useState(null);

  const credentials = useMemo(() => {
    if (!event || !role) return null;
    return getDailyCredentialsForRole(event, role, secrets);
  }, [event, secrets, role]);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      setLoading(true);
      setLoadError(null);
      try {
        if (!eventId) throw new Error("Missing lesson id");
        if (!uid) throw new Error("Not signed in");

        const snap = await getDoc(doc(db, "events", eventId));
        if (!snap.exists()) throw new Error("Lesson not found");

        const data = snap.data();
        const isStudent = data.studentID === uid;
        const isTeacher = data.teacherID === uid;
        if (!isStudent && !isTeacher) {
          throw new Error("You are not part of this lesson");
        }

        if (
          (role === "student" && !isStudent) ||
          (role === "teacher" && !isTeacher)
        ) {
          throw new Error("You are not part of this lesson");
        }

        let secretsData = null;
        try {
          const secretsSnap = await getDoc(doc(db, "eventSecrets", eventId));
          if (secretsSnap.exists()) {
            secretsData = secretsSnap.data();
          }
        } catch {
          // Rules may deny until published; fall back to legacy fields on event.
          secretsData = null;
        }

        if (cancelled) return;
        setSecrets(secretsData);
        setEvent({
          ...data,
          id: snap.id,
          start: data.start?.toDate?.() || data.start,
          end: data.end?.toDate?.() || data.end,
        });
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Failed to load lesson");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEvent();
    return () => {
      cancelled = true;
    };
  }, [eventId, uid, role]);

  useEffect(() => {
    if (!credentials?.roomUrl || !credentials?.token) return undefined;

    const co = DailyIframe.createCallObject({
      subscribeToTracksAutomatically: true,
    });
    setCallObject(co);
    setJoinError(null);

    co.join({
      url: credentials.roomUrl,
      token: credentials.token,
    }).catch((err) => {
      console.error("Daily join failed:", err);
      setJoinError(err?.message || "Failed to join call");
    });

    return () => {
      co.leave().catch(() => {});
      co.destroy().catch(() => {});
      setCallObject(null);
    };
  }, [credentials?.roomUrl, credentials?.token]);

  const handleLeave = async () => {
    try {
      if (callObject) {
        await callObject.leave();
        await callObject.destroy();
      }
    } catch {
      /* ignore */
    }
    setCallObject(null);
    navigate(role === "teacher" ? "/home" : "/home");
  };

  const title =
    role === "teacher"
      ? event?.studentName || event?.title || "Lesson"
      : event?.teacherName || event?.title || "Lesson";

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading lesson…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBanner}>{loadError}</div>
        <div style={{ padding: "0 1rem 1rem" }}>
          <Link className={styles.backLink} to="/home">
            ← Back home
          </Link>
        </div>
      </div>
    );
  }

  if (!credentials) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBanner}>
          This lesson has no Daily video link yet (or it is a legacy Zoom link).
          Book a new lesson after Daily is configured, or open the old join URL
          from your email if you still have one.
        </div>
        <div style={{ padding: "0 1rem 1rem" }}>
          <Link className={styles.backLink} to="/home">
            ← Back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerMeta}>
          <strong>{title}</strong>
          <span>{formatWhen(event?.start, event?.end)}</span>
        </div>
        <Link className={styles.backLink} to="/home">
          ← Exit
        </Link>
      </header>

      {joinError && (
        <div className={styles.errorBanner}>{joinError}</div>
      )}

      {callObject ? (
        <DailyProvider callObject={callObject}>
          <LessonCall role={role} onLeave={handleLeave} />
        </DailyProvider>
      ) : (
        <div className={styles.loading}>Preparing call…</div>
      )}
    </div>
  );
}
